import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  ShieldCheck, FileText, Download, ScrollText, Trash2, AlertTriangle,
  LogIn, LogOut, Shield, KeyRound, Undo2, LifeBuoy, Eye
} from 'lucide-react';
import { API } from '../utils/api';

const DOC_TYPES = [
  { key: 'terms', labelKey: 'privacy.terms' },
  { key: 'privacy_policy', labelKey: 'privacy.privacyPolicy' },
  { key: 'cookie_policy', labelKey: 'privacy.cookiePolicy' },
];

const AUDIT_ICONS = {
  LOGIN: LogIn, LOGOUT: LogOut, CONSENT: Shield, DATA_EXPORT: Download,
  DATA_ACCESS: Eye, PASSWORD_CHANGE: KeyRound, DATA_DELETE_REQUEST: Trash2,
  DATA_DELETE_CANCEL: Undo2, SUPPORT_TICKET_CREATED: LifeBuoy,
};

export function PrivacyDataSection({ headers, user }) {
  const { t, i18n } = useTranslation();
  const [consents, setConsents] = useState(null);
  const [deletion, setDeletion] = useState({ requested: false, scheduled_deletion_date: null });
  const [doc, setDoc] = useState(null);
  const [docType, setDocType] = useState(null);
  const [docLang, setDocLang] = useState(i18n.language === 'sv' ? 'sv' : 'en');
  const [docLoading, setDocLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const seqRef = useRef(0);

  const fetchState = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const [c, d] = await Promise.all([
        axios.get(`${API}/gdpr/consents`, { headers }),
        axios.get(`${API}/gdpr/deletion-status`, { headers }),
      ]);
      if (seq === seqRef.current) {
        setConsents(c.data.consents || {});
        setDeletion(d.data);
      }
    } catch (e) {
      console.error('Failed to fetch privacy state:', e);
    }
  }, [headers]);

  useEffect(() => { fetchState(); }, [fetchState]);

  const toggleMarketing = async (accepted) => {
    seqRef.current++; // invalidate any in-flight fetch so it can't revert this toggle
    try {
      await axios.post(`${API}/gdpr/consent`, { consent_type: 'marketing', accepted }, { headers });
      setConsents(c => ({ ...c, marketing_accepted: accepted }));
      toast.success(t('privacy.consentUpdated'));
    } catch (e) {
      toast.error(t('errors.failedToProcess'));
    }
  };

  const openDoc = async (type, lang) => {
    setDocType(type);
    setDocLang(lang);
    setDocLoading(true);
    setDoc(null);
    try {
      const res = await axios.get(`${API}/gdpr/legal/${type}?language=${lang}`);
      setDoc(res.data);
    } catch (e) {
      toast.error(t('errors.failedToFetch'));
    } finally {
      setDocLoading(false);
    }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await axios.post(`${API}/gdpr/export-data`, { format: 'json' }, { headers });
      const blob = new Blob([JSON.stringify(res.data.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `paymig_data_export_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('privacy.exportReady'));
    } catch (e) {
      toast.error(t('privacy.exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLogs(null);
    try {
      const res = await axios.get(`${API}/gdpr/audit-logs?limit=50`, { headers });
      setAuditLogs(res.data.logs || []);
    } catch (e) {
      setAuditLogs([]);
    }
  };

  const requestDeletion = async () => {
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/gdpr/delete-account`,
        { confirm_email: confirmEmail, reason: reason || undefined }, { headers });
      if (res.data.success) {
        toast.success(t('privacy.deletionScheduled', { date: new Date(res.data.scheduled_deletion_date).toLocaleDateString() }));
        setDeleteOpen(false);
        setConfirmEmail('');
        setReason('');
        fetchState();
      } else {
        toast.error(res.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('privacy.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeletion = async () => {
    try {
      await axios.post(`${API}/gdpr/cancel-deletion`, {}, { headers });
      toast.success(t('privacy.deletionCancelled'));
      fetchState();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('errors.failedToProcess'));
    }
  };

  const requiredRow = (labelKey, checked) => (
    <div className="flex items-center justify-between py-2">
      <div>
        <Label className="text-sm">{t(labelKey)}</Label>
        <p className="text-xs text-muted-foreground">{t('privacy.required')}</p>
      </div>
      <Switch checked={!!checked} onCheckedChange={() => toast.warning(t('privacy.requiredWarning'))} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="privacy-data-section">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-teal-600" /> {t('privacy.title')}
      </h2>

      {deletion.requested && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-center justify-between gap-2" data-testid="deletion-pending-banner">
          <div className="flex items-center gap-2 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {t('privacy.deletionPending', { date: deletion.scheduled_deletion_date ? new Date(deletion.scheduled_deletion_date).toLocaleDateString() : '' })}
          </div>
          <Button size="sm" variant="outline" onClick={cancelDeletion} data-testid="cancel-deletion-btn">
            {t('privacy.cancelDeletion')}
          </Button>
        </div>
      )}

      {/* Consent Preferences */}
      <Card data-testid="consent-preferences-card">
        <CardHeader>
          <CardTitle className="text-base">{t('privacy.consentTitle')}</CardTitle>
          <CardDescription>{t('privacy.consentDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {consents === null ? (
            <div className="space-y-2 py-1" data-testid="consents-loading">
              {[0, 1, 2, 3].map(i => <div key={i} className="animate-pulse bg-gray-100 h-9 rounded" />)}
            </div>
          ) : (
            <>
              {requiredRow('privacy.terms', consents?.terms_accepted)}
              {requiredRow('privacy.privacyPolicy', consents?.privacy_accepted)}
              {requiredRow('privacy.dataProcessing', consents?.data_processing_consent)}
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label className="text-sm">{t('privacy.marketing')}</Label>
                  <p className="text-xs text-muted-foreground">{t('privacy.optional')}</p>
                </div>
                <Switch checked={!!consents?.marketing_accepted} onCheckedChange={toggleMarketing} data-testid="marketing-consent-toggle" />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Legal Documents */}
      <Card data-testid="legal-documents-card">
        <CardHeader>
          <CardTitle className="text-base">{t('privacy.legalTitle')}</CardTitle>
          <CardDescription>{t('privacy.legalDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {DOC_TYPES.map(d => (
            <div key={d.key} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-gray-400" /> {t(d.labelKey)}
              </div>
              <Button variant="ghost" size="sm" onClick={() => openDoc(d.key, docLang)} data-testid={`view-legal-${d.key}`}>
                {t('privacy.view')}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Your Data */}
      <Card data-testid="your-data-card">
        <CardHeader>
          <CardTitle className="text-base">{t('privacy.yourDataTitle')}</CardTitle>
          <CardDescription>{t('privacy.yourDataDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={exportData} disabled={exporting} data-testid="export-data-btn">
            <Download className="w-4 h-4 mr-1" />
            {exporting ? t('privacy.exporting') : t('privacy.exportBtn')}
          </Button>
          <Button variant="outline" size="sm" onClick={openAudit} data-testid="activity-log-btn">
            <ScrollText className="w-4 h-4 mr-1" /> {t('privacy.activityLog')}
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200" data-testid="danger-zone-card">
        <CardHeader>
          <CardTitle className="text-base text-red-600">{t('privacy.dangerTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {deletion.requested ? (
            <Button variant="outline" size="sm" onClick={cancelDeletion} data-testid="danger-cancel-deletion-btn">
              <Undo2 className="w-4 h-4 mr-1" /> {t('privacy.cancelDeletion')}
            </Button>
          ) : (
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="delete-account-btn">
              <Trash2 className="w-4 h-4 mr-1" /> {t('privacy.deleteBtn')}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Legal document viewer */}
      <Dialog open={!!docType} onOpenChange={(o) => !o && setDocType(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="legal-doc-dialog">
          <DialogHeader>
            <DialogTitle>{docType && t(DOC_TYPES.find(d => d.key === docType)?.labelKey)}</DialogTitle>
            <DialogDescription>
              {doc && `${t('privacy.version')} ${doc.version} — ${t('privacy.effective')}: ${doc.effective_date}`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-1 mb-2">
            {['en', 'sv'].map(l => (
              <Button key={l} size="sm" variant={docLang === l ? 'default' : 'outline'}
                onClick={() => openDoc(docType, l)} data-testid={`legal-lang-${l}`}>
                {l.toUpperCase()}
              </Button>
            ))}
          </div>
          <div className="overflow-y-auto flex-1 text-sm text-gray-700 whitespace-pre-line pr-2" data-testid="legal-doc-content">
            {docLoading ? <div className="animate-pulse bg-gray-100 h-40 rounded" /> : doc?.content}
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity log */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="activity-log-dialog">
          <DialogHeader>
            <DialogTitle>{t('privacy.activityLog')}</DialogTitle>
            <DialogDescription>{t('privacy.yourDataDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {auditLogs === null && <div className="animate-pulse bg-gray-100 h-24 rounded" />}
            {auditLogs?.length === 0 && <p className="text-sm text-gray-400 text-center py-4">{t('privacy.noActivity')}</p>}
            {auditLogs?.map((log, i) => {
              const Icon = AUDIT_ICONS[log.action] || FileText;
              return (
                <div key={i} className="flex items-start gap-3 p-2 border rounded-lg" data-testid={`audit-row-${i}`}>
                  <Icon className="w-4 h-4 mt-0.5 text-teal-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{log.action.replaceAll('_', ' ')}</div>
                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="text-xs text-gray-400 truncate">
                        {Object.entries(log.details).map(([k, v]) => `${k}: ${String(v)}`).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 shrink-0">
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete account flow */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t('privacy.deleteWarningTitle')}</DialogTitle>
            <DialogDescription>{t('privacy.deleteWarning')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('privacy.confirmEmailLabel')}</Label>
              <Input value={confirmEmail} onChange={e => setConfirmEmail(e.target.value)}
                placeholder={user?.email} data-testid="confirm-email-input" />
            </div>
            <div>
              <Label>{t('privacy.reasonLabel')}</Label>
              <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} data-testid="delete-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>{t('privacy.close')}</Button>
            <Button variant="destructive" disabled={deleting || confirmEmail !== user?.email}
              onClick={requestDeletion} data-testid="confirm-delete-btn">
              {t('privacy.deleteConfirmBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
